import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { markTutorialComplete } from '../../services/tutorial-service';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const { width, height } = Dimensions.get('window');

interface PartnerRevenueSplitTutorialProps {
  visible: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const tutorialSteps = [
  {
    id: 1,
    title: 'Welcome, Partner!',
    icon: 'cash-outline' as const,
    description: 'Earn revenue sharing from chargers owned by hosts who value your partnership.',
    details: [
      'Receive a percentage of charger earnings',
      'Track your earnings across all shared chargers',
      'Get automatic payouts',
    ],
  },
  {
    id: 2,
    title: 'Your Chargers',
    icon: 'flash-outline' as const,
    description: 'View and manage chargers where you receive revenue splits.',
    details: [
      'Meander Partner Chargers: Revenue share chargers',
      'Observer Chargers: Read-only access chargers',
      'See your split percentage for each charger',
      'View monthly earnings per charger',
    ],
  },
  {
    id: 3,
    title: 'Partner Earnings Dashboard',
    icon: 'analytics-outline' as const,
    description: 'Track your earnings and performance.',
    details: [
      'View current month and total earnings',
      'See earnings breakdown by charger',
      'Monitor active splits across hosts',
      'Track monthly earning trends',
    ],
  },
  {
    id: 4,
    title: 'Payout Information',
    icon: 'card-outline' as const,
    description: 'Set up and manage your payout account.',
    details: [
      'Configure your payout method',
      'View pending payout amounts',
      'Track payout history',
      'See next payout date',
    ],
  },
  {
    id: 5,
    title: 'Stay Informed',
    icon: 'notifications-outline' as const,
    description: 'Get updates about your revenue splits.',
    details: [
      'Notifications when hosts add you to new chargers',
      'Alerts for split percentage changes',
      'Payout status notifications',
      'Monthly earning summaries',
    ],
  },
];

const PartnerRevenueSplitTutorial: React.FC<PartnerRevenueSplitTutorialProps> = ({
  visible,
  onComplete,
  onSkip,
}) => {
  const user = useSelector((state: RootState) => state.user.user);
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      await markTutorialComplete('PARTNER_SPLIT');
    } catch (error) {
      console.error('Error marking tutorial as complete:', error);
    }
    onComplete();
  };

  const handleSkip = async () => {
    try {
      await markTutorialComplete('PARTNER_SPLIT');
    } catch (error) {
      console.error('Error marking tutorial as complete:', error);
    }
    onSkip();
  };

  const step = tutorialSteps[currentStep];
  const progress = ((currentStep + 1) / tutorialSteps.length) * 100;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleSkip}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Partner Revenue Tutorial</Text>
            <TouchableOpacity onPress={handleSkip} style={styles.skipButton}>
              <Text style={styles.skipButtonText}>Skip</Text>
            </TouchableOpacity>
          </View>

          {/* Progress Bar */}
          <View style={styles.progressContainer}>
            <View style={[styles.progressBar, { width: `${progress}%` }]} />
          </View>

          {/* Content */}
          <ScrollView
            style={styles.content}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.iconContainer}>
              <Ionicons name={step.icon} size={64} color="#51B868" />
            </View>

            <Text style={styles.stepTitle}>{step.title}</Text>
            <Text style={styles.stepDescription}>{step.description}</Text>

            <View style={styles.detailsContainer}>
              {step.details.map((detail, index) => (
                <View key={index} style={styles.detailItem}>
                  <Ionicons name="checkmark-circle" size={20} color="#51B868" />
                  <Text style={styles.detailText}>{detail}</Text>
                </View>
              ))}
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity
              onPress={handlePrevious}
              style={[
                styles.button,
                styles.secondaryButton,
                currentStep === 0 && styles.buttonDisabled,
              ]}
              disabled={currentStep === 0}
            >
              <Ionicons
                name="arrow-back"
                size={20}
                color={currentStep === 0 ? '#8D8E90' : '#51B868'}
              />
              <Text
                style={[
                  styles.buttonText,
                  styles.secondaryButtonText,
                  currentStep === 0 && styles.buttonTextDisabled,
                ]}
              >
                Previous
              </Text>
            </TouchableOpacity>

            <View style={styles.stepIndicator}>
              <Text style={styles.stepIndicatorText}>
                {currentStep + 1} / {tutorialSteps.length}
              </Text>
            </View>

            <TouchableOpacity
              onPress={handleNext}
              style={[styles.button, styles.primaryButton]}
            >
              <Text style={[styles.buttonText, styles.primaryButtonText]}>
                {currentStep === tutorialSteps.length - 1 ? 'Get Started' : 'Next'}
              </Text>
              <Ionicons name="arrow-forward" size={20} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#292D33',
    borderRadius: 20,
    width: Math.min(width - 40, 500),
    maxHeight: height - 100,
    minHeight: 400,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1D23',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  skipButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  skipButtonText: {
    color: '#8D8E90',
    fontSize: 14,
    fontWeight: '600',
  },
  progressContainer: {
    height: 4,
    backgroundColor: '#1A1D23',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#51B868',
  },
  content: {
    flexGrow: 1,
    minHeight: 300,
  },
  contentContainer: {
    padding: 24,
    alignItems: 'center',
    flexGrow: 1,
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(81, 184, 104, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  stepTitle: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  stepDescription: {
    color: '#E8E8E9',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  detailsContainer: {
    width: '100%',
    gap: 12,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  detailText: {
    color: '#E8E8E9',
    fontSize: 14,
    flex: 1,
    lineHeight: 20,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#1A1D23',
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  primaryButton: {
    backgroundColor: '#51B868',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#51B868',
  },
  buttonDisabled: {
    borderColor: '#8D8E90',
    opacity: 0.5,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  primaryButtonText: {
    color: '#fff',
  },
  secondaryButtonText: {
    color: '#51B868',
  },
  buttonTextDisabled: {
    color: '#8D8E90',
  },
  stepIndicator: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#1A1D23',
    borderRadius: 12,
  },
  stepIndicatorText: {
    color: '#E8E8E9',
    fontSize: 12,
    fontWeight: '600',
  },
});

export default PartnerRevenueSplitTutorial;

